import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Lightweight in-memory cache for sites.
 * The list changes rarely, so we share it across the app.
 */
let _cache = null;
let _inflight = null;
const _subscribers = new Set();

function _notify() {
  _subscribers.forEach((cb) => {
    try { cb(_cache || []); } catch { /* noop */ }
  });
}

export async function fetchSites(force = false) {
  if (_cache && !force) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/sites").then((r) => r.data).then((list) => {
    _cache = Array.isArray(list) ? list : [];
    _inflight = null;
    _notify();
    return _cache;
  }).catch((e) => {
    _inflight = null;
    throw e;
  });
  return _inflight;
}

export async function invalidateSites() {
  _cache = null;
  return fetchSites(true);
}

export const createSite = (data) => api.post("/sites", data).then((r) => r.data);
export const updateSiteApi = (code, data) => api.patch(`/sites/${code}`, data).then((r) => r.data);
export const deleteSiteApi = (code) => api.delete(`/sites/${code}`).then((r) => r.data);

/**
 * useSites() → { sites, loading, refresh }
 * Sites are the active list only. Include `includeInactive` if you need everything.
 */
export function useSites({ includeInactive = false } = {}) {
  const [sites, setSites] = useState(() => (_cache ? _filter(_cache, includeInactive) : []));
  const [loading, setLoading] = useState(_cache === null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchSites(true);
      setSites(_filter(list, includeInactive));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    let mounted = true;
    if (!_cache) {
      fetchSites().then((list) => {
        if (mounted) {
          setSites(_filter(list, includeInactive));
          setLoading(false);
        }
      }).catch(() => { if (mounted) setLoading(false); });
    } else {
      setSites(_filter(_cache, includeInactive));
      setLoading(false);
    }
    const cb = (list) => mounted && setSites(_filter(list, includeInactive));
    _subscribers.add(cb);
    return () => { mounted = false; _subscribers.delete(cb); };
  }, [includeInactive]);

  return { sites, loading, refresh };
}

function _filter(list, includeInactive) {
  return includeInactive ? list : list.filter((s) => s.active !== false);
}
